import { createAction } from 'redux-actions';

import encodeSearchQuery from 'ha/utils/encodeSearchQuery';
import { getCurrentLanguageCode } from 'ha/modules/LanguageSwitcher/selectors';
import { SupportedCountries } from 'ha/constants/SupportedCountries';
import { Language } from '@ha/intl';
import ApiError from 'ha/services/apiV2/errors/ApiError';
import HttpStatus from 'ha/constants/HttpStatus';
import { getFeatureFlags } from 'ha/modules/FeatureFlags';

import { Actions } from '../constants';
import {
  getSearchParams,
  getPreviousParams,
  getCachedUpdateSearch,
} from '../selectors';

const flow = {
  start: createAction(Actions.UPDATE_SEARCH_START),
  done: createAction(Actions.UPDATE_SEARCH_DONE),
  error: createAction(Actions.UPDATE_SEARCH_ERROR),
};

const formatResponse = (resData, currentLanguageCode) => {
  const langUrlList = Object.values(Language).reduce(
    (result, languageCode) => ({
      ...result,
      ...{ [languageCode]: resData[languageCode].searchUrl },
    }),
    {},
  );

  return {
    city: resData[currentLanguageCode].cityCanonical,
    cityLocalized: resData[currentLanguageCode].cityName,
    country: resData[currentLanguageCode].countryCanonical,
    countryCode: resData[currentLanguageCode].countryCode.toLowerCase(),
    countryLocalized: resData[currentLanguageCode].countryName,
    langURLList: langUrlList,
    latitude: resData[currentLanguageCode].latitude,
    longitude: resData[currentLanguageCode].longitude,
    minZoomLevel: resData[currentLanguageCode].mapMinZoom,
    radius: resData[currentLanguageCode].mapRadius,
    zoomLevel: resData[currentLanguageCode].mapZoom,
    geonameId: resData[currentLanguageCode].geonameId,
    currency: resData[currentLanguageCode].currency,
  };
};

const getCitySearch = (city, country) =>
  encodeSearchQuery(`${city}, ${country}`);

export default () => (dispatch, getState, services) => {
  const state = getState();
  const { citySearch } = getSearchParams(state);
  const { citySearch: prevCitySearch } = getPreviousParams(state);
  const { searchUnsupportedCountries } = getFeatureFlags(state);

  if (citySearch === prevCitySearch) {
    return Promise.resolve(getCachedUpdateSearch(state));
  }

  const currentLanguageCode = getCurrentLanguageCode(getState());

  dispatch(flow.start());

  return services.apiV2
    .searchCity({
      query: citySearch,
      languages: Object.values(Language),
    })
    .then(response => {
      const formattedResponse = formatResponse(
        response.data,
        currentLanguageCode,
      );

      dispatch(flow.done(formattedResponse));

      const { cityName, countryName } = response.data[currentLanguageCode];
      const citySearchFound = getCitySearch(cityName, countryName);

      if (citySearch !== citySearchFound) {
        return Promise.reject(
          new ApiError(HttpStatus.NOT_FOUND, 'Missmatched City Country', {}),
        );
      }

      if (
        searchUnsupportedCountries === 'on' &&
        !SupportedCountries[formattedResponse.countryCode.toUpperCase()]
      ) {
        throw new ApiError(HttpStatus.GONE, 'Unsupported Country', {});
      }

      return formattedResponse;
    })
    .catch(errors => {
      if (
        errors.status !== HttpStatus.NOT_FOUND &&
        errors.status !== HttpStatus.GONE
      ) {
        dispatch(flow.error(errors));
      }

      return Promise.reject(errors);
    });
};
